import React, { useCallback, useState } from "react";
import clsx from "clsx";
import { useFormContext } from "react-hook-form";

import {
	HookFormInputGroup,
	HookSelectAsyncFormGroup,
} from "components/hookForms";
import { FormColumn, FormRow } from "components/layout";
import { DebounceTimeout } from "globalConstants";
import { trimMidSentenceNormalizer, trimNormalizer } from "validators";
import {
	maxLength150,
	maxLength17,
	maxLength50,
	minLength6,
	normalizeAll,
	onlyAlphanumericAndSpecialCharacters,
	onlyDigits,
	required,
} from "validators/validation";

import { getModalInputStyles, getModalSelectStyles } from "./getModalStyles";
import { loadBankRoutings } from "./loadBankRoutings";
import {
	accountNumberFieldName,
	bankAccountFieldName,
	bankFieldName,
	routingNumberFieldName,
} from "./paymentMethodConstants";

import styles from "./paymentMethod.module.scss";

type AchFieldsProps = {
	isLoading: boolean;
	isModal: boolean;
};

export const AchFields = ({ isLoading, isModal }: AchFieldsProps) => {
	const {
		register,
		formState: { errors },
		setValue,
	} = useFormContext();
	const bankRoutings = useCallback(loadBankRoutings, []);
	const [isDisabledBank, setIsDisabledBank] = useState<boolean>(true);

	const handleRoutingChange = ({ bank }) => {
		setValue(bankFieldName, bank, {
			shouldValidate: true,
			shouldDirty: true,
		});
		setIsDisabledBank(!!bank);
	};

	const selectStyle = getModalSelectStyles(isModal);
	const inputStyle = getModalInputStyles(isModal);

	return (
		<>
			<FormRow>
				<FormColumn
					className={clsx(isModal && styles.modalFormColumnFull)}
				>
					<HookSelectAsyncFormGroup
						labelSize={selectStyle}
						controlSize={selectStyle}
						name={routingNumberFieldName}
						label="Routing Number (ABA)"
						getOptionLabel={option => option?.label}
						getOptionValue={option => option?.value}
						loadOptions={bankRoutings}
						additional={{
							page: 0,
							pageSize: 5,
						}}
						placeholder="Please Select"
						debounceTimeout={DebounceTimeout}
						blurLoading={isLoading}
						cacheOptions
						isSearchable
						defaultOptions
						rules={{
							validate: {
								required,
							},
						}}
						showRequiredMark
						onChange={handleRoutingChange}
					/>
				</FormColumn>
				<FormColumn
					className={clsx(isModal && styles.modalFormColumnFull)}
				>
					<HookFormInputGroup
						colLabel={inputStyle}
						colInput={inputStyle}
						name={accountNumberFieldName}
						register={register}
						label="Account Number (DDA)"
						blurLoading={isLoading}
						autoComplete="off"
						validator={{
							required,
							minLength6,
							maxLength17,
						}}
						normalize={onlyDigits}
						showRequiredMark
						normalizeOnBlur={trimNormalizer}
						errors={errors}
					/>
				</FormColumn>
			</FormRow>
			<FormRow>
				<FormColumn
					className={clsx(isModal && styles.modalFormColumnHalf)}
				>
					<HookFormInputGroup
						colLabel={inputStyle}
						colInput={inputStyle}
						name={bankAccountFieldName}
						register={register}
						label="Bank Account Name"
						blurLoading={isLoading}
						autoComplete="off"
						validator={{
							required,
							maxLength150,
						}}
						showRequiredMark
						normalizeOnBlur={trimNormalizer}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						errors={errors}
					/>
				</FormColumn>
				<FormColumn
					className={clsx(isModal && styles.modalFormColumnHalf)}
				>
					<HookFormInputGroup
						colLabel={inputStyle}
						colInput={inputStyle}
						name={bankFieldName}
						register={register}
						label="Bank"
						blurLoading={isLoading}
						autoComplete="off"
						normalizeOnBlur={trimNormalizer}
						errors={errors}
						validator={{ maxLength50 }}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						readOnly={isDisabledBank}
					/>
				</FormColumn>
			</FormRow>
		</>
	);
};
